export const AR_SLIDER_FILENAME = 'arSlider.js';

// Plain script string: eval'd in the editor preview (global-mode p5) AND in the
// AR viewer (via new Function). Must be idempotent on re-eval. `window.__arViewer`
// (set by ar-viewer) means "AR mode": do not create DOM controls; the viewer app renders them.
export const AR_SLIDER_SOURCE = `
(function (global) {
  if (global.__arSliders) { /* keep registry across re-runs (persist) */ }
  var reg = global.__arSliders || {
    _defs: {}, _persist: {}, _resetAll: false, _resetPending: {},
    _onChange: null, _onRegister: null, _applying: false,
    // Preview-only lil-gui preference set by enableLilGui()/disableLilGui():
    // null = auto (use lil-gui when present), true = force on (lazy-load if
    // needed), false = force off (fall back to bare p5 sliders). Ignored in the
    // AR viewer, where panel visibility is governed by arSettings.showGuiInAr.
    _guiEnabled: null,
    value: function (name) {
      var d = this._defs[name];
      return d ? d.value : undefined;
    },
    setFromControl: function (name, v) {
      if (this._applying) return;
      var d = this._defs[name];
      if (!d) return;
      v = Number(v);
      d.value = v; this._persist[name] = v;
      if (this._onChange) this._onChange(name, v);
    },
    applyRemote: function (name, v) {
      v = Number(v);
      this._persist[name] = v;
      var d = this._defs[name];
      if (!d) return;
      this._applying = true;
      d.value = v;
      if (d.control && d.control.__set) { try { d.control.__set(v); } catch (e) {} }
      this._applying = false;
    },
    reset: function (name) {
      if (name == null) { this._resetAll = true; this._persist = {}; }
      else { this._resetPending[name] = true; delete this._persist[name]; }
    },
    register: function (spec) {
      var name = spec.name;
      var min = Number(spec.min), max = Number(spec.max);
      var step = spec.step == null ? 1 : Number(spec.step);
      var def = spec.value == null ? min : Number(spec.value);
      var wasReset = this._resetAll || !!this._resetPending[name];
      var resolved = wasReset ? def : (name in this._persist ? this._persist[name] : def);
      var entry = { name: name, min: min, max: max, step: step, value: resolved, control: null };
      this._defs[name] = entry;
      this._persist[name] = resolved;
      delete this._resetPending[name];
      var self = this;
      if (!global.__arViewer) { attachControl(self, entry); }
      if (this._onRegister) this._onRegister(entry);
      if (wasReset && this._onChange) this._onChange(name, resolved);
      return {
        min: min, max: max, elt: entry.control && entry.control.elt,
        value: function (v) {
          if (v === undefined) return self._defs[name].value;
          self.setFromControl(name, v);
          return self._defs[name].value;
        }
      };
    }
  };
  // Editor preview: the host injects window.__arSliderInitialValues (the session's
  // stored Firestore sliderValues) synchronously BEFORE the sketch runs. Seed a
  // freshly-created registry's _persist from it so register() resolves to the stored
  // values on the very first setup() (persist-by-default) — the preview iframe is a
  // fresh window on every Run, so without this it would always start at code defaults.
  // Only seeds a NEW registry (not one reused across re-eval); resetArSliders() still
  // wins since it runs in setup() and clears _persist. Absent global (AR viewer, which
  // seeds via app.js applyStoredSliderValues; tests) → no-op.
  if (!global.__arSliders && global.__arSliderInitialValues) {
    Object.keys(global.__arSliderInitialValues).forEach(function (name) {
      var v = Number(global.__arSliderInitialValues[name]);
      if (!isNaN(v)) reg._persist[name] = v;
    });
  }

  // Clear the one-shot reset flag once a full setup() pass has registered sliders.
  reg._resetAll = false;

  // Keep in sync with lilGuiURL in common/p5URLs.js — enableLilGui() lazily loads
  // the library from code when the <script> tag isn't already present (e.g. the
  // Manage Libraries toggle is off and the auto-injected tag was removed).
  var LIL_GUI_URL =
    'https://cdn.jsdelivr.net/npm/lil-gui@0.19/dist/lil-gui.umd.min.js';

  function attachControl(self, entry) {
    // Preview (global-mode p5): prefer a shared lil-gui panel when available and
    // not force-disabled, else fall back to a bare p5 DOM slider.
    if (global.lil && global.lil.GUI && self._guiEnabled !== false) {
      var gui = global.__arGui || (global.__arGui = new global.lil.GUI({ title: 'AR Sliders' }));
      var proxy = {}; proxy[entry.name] = entry.value;
      var ctrl = gui.add(proxy, entry.name, entry.min, entry.max, entry.step);
      ctrl.onChange(function (v) { self.setFromControl(entry.name, v); });
      entry.control = {
        elt: null,
        __set: function (v) { proxy[entry.name] = v; ctrl.updateDisplay(); }
      };
      return;
    }
    if (typeof global.createSlider !== 'function') return;
    var s = global.createSlider(entry.min, entry.max, entry.value, entry.step);
    entry.control = {
      elt: s.elt,
      __set: function (v) { s.value(v); }
    };
    s.input(function () { self.setFromControl(entry.name, s.value()); });
  }

  // Rebuild every registered slider's control — used after lil-gui loads late, or
  // after enableLilGui()/disableLilGui() flips which rendering path attachControl
  // takes. Tears down each old control's DOM (fallback p5 slider) and, when
  // switching into lil-gui mode, destroys the shared panel so it's rebuilt once
  // (no duplicate rows). No-op in the AR viewer (it renders its own controls).
  function reattachAll(self) {
    if (global.__arViewer) return;
    if (
      self._guiEnabled !== false &&
      global.lil && global.lil.GUI &&
      global.__arGui && global.__arGui.destroy
    ) {
      try { global.__arGui.destroy(); } catch (e) {}
      global.__arGui = null;
    }
    Object.keys(self._defs).forEach(function (name) {
      var entry = self._defs[name];
      if (entry.control && entry.control.elt && entry.control.elt.remove) {
        try { entry.control.elt.remove(); } catch (e) {}
      }
      entry.control = null;
      attachControl(self, entry);
    });
  }

  // Ensure lil-gui is loaded, then invoke onready. Immediate when window.lil.GUI
  // already exists (the usual case — the tag is auto-injected when the helper is
  // added); otherwise inject the CDN <script> once and wait for its load event.
  function loadLilGui(onready) {
    if (global.lil && global.lil.GUI) { onready(); return; }
    if (typeof document === 'undefined') return;
    var existing = document.getElementById('__arLilGuiScript');
    if (existing) { existing.addEventListener('load', onready); return; }
    var s = document.createElement('script');
    s.id = '__arLilGuiScript';
    s.src = LIL_GUI_URL;
    s.addEventListener('load', onready);
    document.head.appendChild(s);
  }

  global.__arSliders = reg;
  global.createArSlider = function (name, min, max, value, step) {
    return reg.register({ name: name, min: min, max: max, value: value, step: step });
  };
  global.resetArSliders = function (name) { reg.reset(name); };

  // Activate the lil-gui panel from sketch code (e.g. in setup()) instead of the
  // Manage Libraries toggle. No-op in the AR viewer, where the panel is governed
  // by arSettings.showGuiInAr. Lazily loads lil-gui if it isn't present yet.
  global.enableLilGui = function () {
    if (global.__arViewer) return;
    reg._guiEnabled = true;
    loadLilGui(function () { reattachAll(reg); });
  };
  // The inverse: hide the lil-gui panel in the preview; existing sliders fall back
  // to bare p5 sliders. No-op in the AR viewer (showGuiInAr owns AR visibility).
  global.disableLilGui = function () {
    if (global.__arViewer) return;
    reg._guiEnabled = false;
    if (global.__arGui && global.__arGui.destroy) {
      try { global.__arGui.destroy(); } catch (e) {}
    }
    global.__arGui = null;
    reattachAll(reg);
  };
})(window);
`;
